/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, April 5th 2022, 7:48:03 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import { PrimaryKeyValue } from "lib/types";
import {
  APIOne2ManyListEditorFormInput,
  GenericAPIOne2ManyListEditorFormInputProps,
} from "./APIOne2ManyListEditorFormInput";
import Grid, { GridSize } from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import { OSActionButton, useToggleState } from "ui-widgets";
import { useAPIForm, UseAPIFormSpec } from "lib/api";
import { AutoSaveAPIForm } from "ui-widgets/forms";
import { invariant } from "immutability-helper";
import { IListEditorContext } from "lib/providers/advanced-list-editor/ListEditorProvider";
import { DeleteOSItemDialog } from "version-control/components/DeleteOSItemDialog";
import { useDeleteOSItem } from "version-control/services/DeleteOSItemAPI";

const getGridDimensions = (numberOfItems: number): { containerColumns: number; itemColumns: GridSize } => ({
  containerColumns: numberOfItems <= 12 ? 12 : numberOfItems,
  itemColumns: (numberOfItems <= 12 ? Math.floor(12 / numberOfItems) : 1) as GridSize,
});

type HeaderCellComponent = React.ReactNode;

type BodyCellComponent = React.ReactNode;

interface renderBodyCellsArgs<TRelatedObject> {
  rowItem: TRelatedObject;
  updateList: IListEditorContext<TRelatedObject>["updateList"];
}

interface TabulatedOne2ManyInlineInputProps<TPrimaryObject, TRelatedObject>
  extends Omit<GenericAPIOne2ManyListEditorFormInputProps<TPrimaryObject, TRelatedObject>, "renderListView"> {
  headerCells: HeaderCellComponent[];
  renderBodyCells: (args: renderBodyCellsArgs<TRelatedObject>) => BodyCellComponent[];
  getNewItemDefaultValues: () => TRelatedObject;
  relatedObjectValidationSchema: UseAPIFormSpec<TRelatedObject>["apiFormDataValidationSchema"];
  renderInlineFields?: (item: TRelatedObject) => React.ReactNode[];
  renderNoDataMessage?: (onClickNewItem: () => void) => React.ReactNode;
}

/**
 * Uses the APIOne2ManyListEditorFormInput component for logic.
 *
 * The form for the current selected item is rendered as a row in the grid table created. Whenever a
 * selected row is edited, a PUT request is triggered to the same apiPath provided.
 *
 * The itemCreationQueue in our ListEditorProvided triggers a post request for each item in the queue.
 *
 * Remember our requests are all asynchronous so this all happens seemingly concurrently.
 *
 */

export const TabulatedOne2ManyInlineInput = <TPrimaryObject, TRelatedObject>(
  props: TabulatedOne2ManyInlineInputProps<TPrimaryObject, TRelatedObject>
) => {
  const { handleClose: handleCloseDelete, handleOpen: handleOpenDelete, open: openDelete } = useToggleState();

  const [itemIdToDelete, setItemIdToDelete] = React.useState<PrimaryKeyValue<TRelatedObject>>();

  const [deleteStatus, setDeletedStatus] = React.useState(false);

  const [restoredItem, setRestoredItem] = React.useState<TRelatedObject>();

  const useAPIDeleteOSItem = () => useDeleteOSItem(props.apiServicePath);

  const handleItemDeleteRequest = (args: { itemIdToDelete: PrimaryKeyValue<TRelatedObject> }) => {
    setItemIdToDelete(args.itemIdToDelete);
    handleOpenDelete();
  };

  return (
    <APIOne2ManyListEditorFormInput<TPrimaryObject, TRelatedObject>
      apiServicePath={props.apiServicePath}
      foreignKey={props.foreignKey}
      foreignKeyId={props.foreignKeyId}
      uniqueIdentifier={props.uniqueIdentifier}
      renderListView={({
        itemsListState,
        selectItem,
        createNewItem,
        updateList,
        updateListAndSelectIfNewItem,
        removeItem,
      }) => {
        if (restoredItem) {
          updateList(restoredItem);
          setRestoredItem(null);
        }

        const onClickNewItem = () => createNewItem(props.getNewItemDefaultValues());

        const shouldRenderNoDataMessage =
          itemsListState.savedItems.length === 0 &&
          itemsListState.itemCreationQueue.length === 0 &&
          props.renderNoDataMessage;

        return shouldRenderNoDataMessage ? (
          <>{props.renderNoDataMessage?.(onClickNewItem)}</>
        ) : (
          <>
            <GridHeaderRow headerCells={props.headerCells} />
            {itemsListState.savedItems.map((savedItem) => {
              if (itemIdToDelete && itemIdToDelete === savedItem[props.uniqueIdentifier] && deleteStatus) {
                removeItem(savedItem[props.uniqueIdentifier]);
                setDeletedStatus(false);
              }

              return (
                <>
                  {props.renderInlineFields && itemsListState.selectedItemId === savedItem[props.uniqueIdentifier] ? (
                    <TRelatedObjectInlineForm
                      key={`${savedItem[props.uniqueIdentifier]}`}
                      apiServicePath={props.apiServicePath}
                      uniqueIdentifier={props.uniqueIdentifier}
                      relatedObjectValidationSchema={props.relatedObjectValidationSchema}
                      apiFormDefaultValues={savedItem}
                      updateItemFromAPICallback={updateList}
                      renderInlineFields={props.renderInlineFields}
                      handleItemDeleteRequest={handleItemDeleteRequest}
                    />
                  ) : (
                    <GridBodyRow<TRelatedObject>
                      key={`${savedItem[props.uniqueIdentifier]}`}
                      onClick={() => selectItem(savedItem[props.uniqueIdentifier])}
                      headerCellsCount={props.headerCells.length}
                      renderBodyCells={props.renderBodyCells}
                      rowItem={savedItem}
                      uniqueIdentifier={props.uniqueIdentifier}
                      updateList={updateList}
                      selectedItemId={itemsListState.selectedItemId}
                      setItemIdToDelete={setItemIdToDelete}
                      handleItemDeleteRequest={handleItemDeleteRequest}
                    />
                  )}
                </>
              );
            })}

            {itemsListState.itemCreationQueue.map((newItem, index) => (
              <TRelatedObjectInlineForm
                key={`new-item-${newItem.os_unsaved_item_temp_unique_id}`}
                apiServicePath={props.apiServicePath}
                uniqueIdentifier={props.uniqueIdentifier}
                relatedObjectValidationSchema={props.relatedObjectValidationSchema}
                apiFormDefaultValues={newItem}
                updateItemFromAPICallback={updateListAndSelectIfNewItem}
                renderInlineFields={props.renderInlineFields}
                handleItemDeleteRequest={null}
              />
            ))}

            <OSActionButton actionStyle="add" buttonType="floating" onClick={onClickNewItem} sx={{ mt: 1 }} />

            <DeleteOSItemDialog<TRelatedObject>
              useDeleteOSItem={useAPIDeleteOSItem}
              itemId={itemIdToDelete}
              onDelete={() => setDeletedStatus(true)}
              openDialog={openDelete}
              handleCloseDialog={handleCloseDelete}
              onRestore={setRestoredItem}
            />
          </>
        );
      }}
    />
  );
};

type GridHeaderRowProps<TRelatedObject> = Pick<TabulatedOne2ManyInlineInputProps<any, TRelatedObject>, "headerCells">;

const GridHeaderRow = <TRelatedObject,>(props: GridHeaderRowProps<TRelatedObject>) => {
  // +1 is for the delete action
  const { containerColumns, itemColumns } = getGridDimensions(props.headerCells.length + 1);

  return (
    <Grid container columns={containerColumns} className="tabulated-one2many-head-row">
      {props.headerCells.map((headerCellLabelComponent, columnPosition) => {
        return (
          <Grid key={`header-cell-${columnPosition}`} className="tabulated-one2many-head-cell" item md={itemColumns}>
            {headerCellLabelComponent}
          </Grid>
        );
      })}
      <Grid key={`header-cell-delete-action`} className="tabulated-one2many-head-cell" item md={itemColumns}>
        Delete
      </Grid>
    </Grid>
  );
};

interface GridBodyRowProps<TRelatedObject>
  extends Pick<TabulatedOne2ManyInlineInputProps<any, TRelatedObject>, "renderBodyCells" | "uniqueIdentifier">,
    renderBodyCellsArgs<TRelatedObject> {
  headerCellsCount: number;
  selectedItemId: PrimaryKeyValue<TRelatedObject>;
  setItemIdToDelete: (itemIdToDelete: PrimaryKeyValue<TRelatedObject>) => void;
  handleItemDeleteRequest: (args: { itemIdToDelete: PrimaryKeyValue<TRelatedObject> }) => void;
  onClick: () => void;
}

const GridBodyRow = <TRelatedObject,>(props: GridBodyRowProps<TRelatedObject>) => {
  const { rowItem, updateList, handleItemDeleteRequest } = props;

  const bodyCells = props.renderBodyCells({ rowItem, updateList });

  invariant(
    typeof props.headerCellsCount === "number" && props.headerCellsCount === bodyCells.length,
    () =>
      `RENDERING ERROR: You must provide the same number of table headers, table cells and
      inline fields to render the TabulatedOne2ManyInlineInput component. Kindly note that your
      inline fields should be provided as the children of this component.
      Current Header count: ${props.headerCellsCount}. Current Column Count ${bodyCells.length}`
  );

  // +1 is for the delete column
  const { containerColumns, itemColumns } = getGridDimensions(bodyCells.length + 1);

  return (
    <Grid
      container
      onClick={props.onClick}
      columns={containerColumns}
      className={`tabulated-one2many-body-row${
        props.rowItem[props.uniqueIdentifier] === props.selectedItemId ? " selected" : ""
      }`}
    >
      {bodyCells.map((bodyCell, columnPosition) => {
        return (
          <>
            <Grid
              key={`body-cell-${props.rowItem[props.uniqueIdentifier]}-${columnPosition}`}
              className="tabulated-one2many-body-cell"
              item
              md={itemColumns}
            >
              {bodyCell}
            </Grid>
          </>
        );
      })}
      <Grid className="tabulated-one2many-body-cell" item md={itemColumns}>
        <IconButton
          edge="end"
          aria-label="delete"
          onClick={() => handleItemDeleteRequest({ itemIdToDelete: props.rowItem[props.uniqueIdentifier] })}
          sx={{ height: 20, width: 20 }}
        >
          <DeleteIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};

const NewGridBodyRowSkeleton = () => <Skeleton variant="rectangular" />;

interface UseTRelatedObjectAPIFormSpec<TRelatedObject>
  extends Pick<
    TabulatedOne2ManyInlineInputProps<any, TRelatedObject>,
    "apiServicePath" | "uniqueIdentifier" | "relatedObjectValidationSchema" | "renderInlineFields"
  > {
  apiFormDefaultValues: UseAPIFormSpec<TRelatedObject>["apiFormDefaultValues"];
  updateItemFromAPICallback: UseAPIFormSpec<TRelatedObject>["setAPIFormResponse"];
  handleItemDeleteRequest: (args: { itemIdToDelete: PrimaryKeyValue<TRelatedObject> }) => void;
}

const TRelatedObjectInlineForm = <TRelatedObject,>({
  apiServicePath,
  uniqueIdentifier,
  apiFormDefaultValues,
  updateItemFromAPICallback,
  relatedObjectValidationSchema,
  renderInlineFields,
  handleItemDeleteRequest,
}: UseTRelatedObjectAPIFormSpec<TRelatedObject>) => {
  const useAPIFormReturn = useAPIForm({
    apiServicePath,
    apiFormDataValidationSchema: relatedObjectValidationSchema,
    apiFormDefaultValues,
    apiInitialFetchId: apiFormDefaultValues[uniqueIdentifier],
    primaryKey: uniqueIdentifier,
    setAPIFormResponse: updateItemFromAPICallback,
  });

  const childrenArray = renderInlineFields?.(apiFormDefaultValues) ?? [<NewGridBodyRowSkeleton />];

  const { containerColumns, itemColumns } = getGridDimensions(childrenArray.length + 1);

  return (
    <AutoSaveAPIForm
      showFeedbackAlerts={false}
      useAPIFormReturn={useAPIFormReturn}
      loaderType="none"
      showSubmitButton={false}
      doDebounce={
        false
      } /** Since we are rendering the form on a row, we do not debounce to avoid missing a callback when user switches to another row, which unmounts this form and mounts form for other selected row. */
    >
      <Grid
        container
        columns={containerColumns}
        className="tabulated-one2many-body-row selected"
        sx={apiFormDefaultValues[uniqueIdentifier] === null ? { opacity: 0.3, pointerEvents: "none" } : null}
      >
        {childrenArray.map((child, index) => {
          return (
            <Grid
              key={`field-input-cell-component-${index}`}
              className="tabulated-one2many-body-cell active"
              item
              md={itemColumns}
            >
              {child}
            </Grid>
          );
        })}
        {handleItemDeleteRequest && apiFormDefaultValues[uniqueIdentifier] && (
          <Grid className="tabulated-one2many-body-cell" item md={itemColumns}>
            <IconButton
              edge="end"
              aria-label="delete"
              onClick={() => handleItemDeleteRequest({ itemIdToDelete: apiFormDefaultValues[uniqueIdentifier] })}
              sx={{ height: 20, width: 20 }}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>
    </AutoSaveAPIForm>
  );
};
